import { useDispatch, useSelector } from "react-redux";
import { Outlet } from "react-router-dom";

// material-ui
import {
  AppBar,
  Box,
  CircularProgress,
  CssBaseline,
  Toolbar,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";

// project imports
import Header from "./Header";
import DataPrivacyBanner from "./DataPrivacyBanner";
import Sidebar from "./Sidebar";
import navigation from "./Sidebar/menu-items";

// assets
import { IconChevronRight } from "@tabler/icons";
import config from "../../../../../config";
import logoExtended from "../../../../assets/images/paisley-background-dark.jpg";
import { menuVisible } from "../../../../redux/actions/session";
import Breadcrumbs from "../../widgets/extended/Breadcrumbs";
import { StateStatus } from "../../../../../domain/entities/State";
import { defaultStylePage, stylePageConfigs } from "./stylePageConfigs";
import { useAuth } from "../../../../hooks/useAuth";
import { ConfigPageIndex } from "../../../../../domain/entities/Configs";
import { useEffect } from "react";
import { loadConfigs } from "../../../../redux/actions/administrator";
import { RoleLevel } from "../../../../../domain/entities/Role";
// import ActionsButton from "../../../pages/signatory/ActionsButton";

// styles
const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" && prop !== "themeConfig" })(
  ({ theme, open, themeConfig }) => {
    const stylePageConfig = stylePageConfigs[themeConfig] || stylePageConfigs[defaultStylePage];
    return {
      ...theme.typography.mainContent,
      marginRight: 10,
      backgroundImage: stylePageConfig.backgroundImage,
      backgroundRepeat: stylePageConfig.backgroundRepeat || "repeat",
      backgroundSize: stylePageConfig.backgroundSize || "auto",
      position: "relative",
      "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: stylePageConfig.overlay || "rgba(255, 255, 255, 0.9)",
        zIndex: 1,
      },
      zIndex: 0,
      borderRadius: 6,
      transition: theme.transitions.create(
        "margin",
        open
          ? {
              easing: theme.transitions.easing.easeOut,
              duration: theme.transitions.duration.enteringScreen,
            }
          : {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }
      ),
      [theme.breakpoints.up("md")]: {
        marginLeft: open ? 0 : -(config.drawerWidth - 20),
        width: `calc(100% - ${config.drawerWidth}px)`,
      },
      [theme.breakpoints.down("md")]: {
        marginLeft: "20px",
        width: `calc(100% - ${config.drawerWidth}px)`,
        padding: "16px",
      },
      [theme.breakpoints.down("sm")]: {
        marginLeft: "0px",
        width: `100%`,
        padding: "10px",
        marginRight: "0px",
      },
    };
  }
);

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const dispatch = useDispatch();
  // Handle left drawer
  const leftDrawerOpened = useSelector((state) => state.session.menuVisible);
  const siteConfigs = useSelector((state) => state.administrator.siteConfigs);
  const siteConfigsLoad = useSelector((state) => state.administrator.siteConfigsLoad);
  const currentThemeConfig = siteConfigs?.siteConfigs.stylePage || "Paisley";
  const handleLeftDrawerToggle = () => {
    dispatch(menuVisible(!leftDrawerOpened));
  };
  
  useEffect(() => {
    if (user?.organization?.id) {
      if (
        !siteConfigs?.siteConfigs &&
        user &&
        typeof user.role === "object" &&
        "level" in user.role &&
        user.role.level > RoleLevel.Signatory
      ) {
        dispatch(
          loadConfigs(user.organization.id, ConfigPageIndex.CONFIGURATIONS)
        );
      }
    }
  }, []);
  
  if (
    !siteConfigs?.siteConfigs &&
    user &&
    typeof user.role === "object" &&
    "level" in user.role &&
    user.role.level > RoleLevel.Signatory
  ) {
    return (
      <Box
        sx={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", position: "static" }}>
      <CssBaseline />
      {/* header */}
      <AppBar
        enableColorOnDark
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          backgroundImage:
            "linear-gradient(to right, #CC4C77 0%, #5C5B9B 100%)",
          transition: leftDrawerOpened
            ? theme.transitions.create("width")
            : "none",
        }}
      >
        <Toolbar>
          <Header handleLeftDrawerToggle={handleLeftDrawerToggle} />
        </Toolbar>
      </AppBar>

      {/* drawer */}
      <Sidebar
        drawerOpen={leftDrawerOpened}
        drawerToggle={handleLeftDrawerToggle}
      />

      {/* main content */}
      <Main theme={theme} open={leftDrawerOpened} themeConfig={currentThemeConfig}>
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            marginLeft: !leftDrawerOpened ? "45px" : null,
          }}
        >
          {/* breadcrumb */}
          <Breadcrumbs
            separator={IconChevronRight}
            navigation={navigation}
            icon
            title
            rightAlign
          />
          <DataPrivacyBanner />
          <Outlet />
        </Box>
      </Main>
      
      {/* Fixed Actions Button */}
      <Box
        sx={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          zIndex: 1000,
        }}
      >
     {/*<ActionsButton /> */}
      </Box>
      
     {/* } {process.env.REACT_APP_ENV === Environment.DEV && <Customization />} */}
    </Box>
  );
};

export default MainLayout;
